<template>
    <div class="w-full mb-4 relative">
        <!-- Header with Back Link and Title -->
        <div class="mb-4">
            <router-link to="/settings" class="text-blue-600 hover:text-blue-800">
                <font-awesome-icon icon="fas fa-arrow-left" class="text-lg" />
            </router-link>
            <h2 class="text-xl font-semibold text-gray-800 dark:text-white text-center -mt-4">{{ $t('permissions') }}</h2>
        </div>

        <div class="flex w-full gap-5">
            <section class="-ml-4 p-4 bg-white dark:bg-dark.bkg shadow-light dark:shadow-dark border border-sepBorder dark:border-dark.sepBorder rounded-lg">
                <h3 class="text-sm text-mazUiText dark:text-dark.mazUiText">{{ $t('positions') }}</h3>
                <div class="overflow-x-auto">
                    <slot v-for="(position,index) in positions" :key="index">
                        <div @click="selectedPosition = index" class="flex justify-between items-center bg-button p-1.5 px-3 hover:bg-opacity-100 hover:text-white dark:hover:text-white border border-sepBorder dark:border-dark.sepBorder cursor-pointer my-2 rounded-lg" 
                            :class="{'text-white dark:text-white': selectedPosition === index, 'bg-opacity-30 text-button dark:text-dark.mazUiText': selectedPosition !== index}">
                            <div v-if="position.custom_name" class="leading-0">
                                {{ position.custom_name }}
                                <div class="text-xs font-lighter mt-0 capitalize">{{ position.name?.replace("_", " ") }}</div>
                            </div>
                            <span v-else class="capitalize">{{ position.name?.replace("_", " ") }}</span>
                        </div>
                    </slot>
                </div>
                <button @click="openAddPositionModal()" class="border-2 border-growth text-growth hover:scale-105 transition-all font-semibold py-1.5 px-4 rounded-lg my-2 w-full">
                    <font-awesome-icon :icon="['fas', 'plus']" /> {{ $t('add_a_position') }}
                </button>
            </section>

            <section v-if="selectedPosition >= 0" class="bg-white dark:bg-dark.bkg shadow-light dark:shadow-dark border border-sepBorder dark:border-dark.sepBorder rounded-lg p-2">
                <h3 class="p-2">{{ $t('permissions_for_position') }} <span class="capitalize font-semibold">{{ selectedPosition >= 0 ? positions[selectedPosition]?.name?.replace("_", " ") : null }}</span></h3>
                <div class="flex flex-col gap-2">
                    <div v-for="category in permission_categories" :key="category.id" class="p-1 px-2 cursor-pointer rounded-lg">
                        <div class="border border-sepBorder dark:border-dark.sepBorder rounded-lg p-3 pt-0 px-4 w-72">
                            <div class="flex justify-between items-center cursor-pointer -mx-4 px-4 py-2 bg-mazUiBg dark:bg-dark.mazUiBg" :class="{'border-b border-sepBorder dark:border-dark.sepBorder': !category.hidden}" @click="category.hidden = !category.hidden">
                                <span class="text-sm text-mazUiText dark:text-dark.mazUiText">{{ category.category_name }}</span>
                                <font-awesome-icon :icon="['fas', category.hidden ? 'angle-down' : 'angle-up']" />
                            </div>
                            <transition name="accordion">
                                <div v-if="!category.hidden" class="accordion-content flex flex-col gap-1.5 mt-2">
                                    <div v-for="permission in category.permissions" :key="permission.id" class="flex justify-between items-center cursor-pointer rounded-lg">
                                        <div class="flex">
                                            <div class="text-gray-800 dark:text-white capitalize truncate w-max">
                                                {{ permission.name?.replace("_", " ") }}
                                            </div>
                                            <div class="w-max" v-tooltip.bottom="permission.description">
                                                <font-awesome-icon icon="far fa-question-circle" class="text-gray-400 ml-1.5" />
                                            </div>
                                        </div>
                                        <MazSwitch v-model="permission.status" />
                                    </div>
                                </div>
                            </transition>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    </div>

    <transition name="zoom-fade">
        <section v-if="modal.display" @click="modal.display = false" class="backdrop-blur w-full h-full fixed inset-0 bg-gray-700 bg-opacity-30 z-50 flex justify-center items-center px-4 text-black">
            <section @click.stop class="bg-white dark:bg-dark.bkg dark:text-dark.textOnBkg rounded-lg max-h-screen mx-auto my-auto p-8 px-6 sm:p-10 z-50 overflow-auto relative" :class="modal.type == 'add_batch_creators' ? 'max-w-screen' : (modal.type == 'graph' ? 'sm:max-w-7xl' : (modal.type == 'session_electron' ? 'sm:max-w-3xl' : 'sm:max-w-5xl'))">
                <font-awesome-icon @click="modal.display = false" icon="fas fa-xmark" class="transition absolute p-6 top-0 right-0 text-xl cursor-pointer hover:opacity-50" />
                <add-position v-if="modal.type == 'add_position'"  />
            </section>
        </section>
    </transition>
</template>

<script setup>
    import { vTooltip } from 'maz-ui'
</script>

<script>
    import { company_positions_list, get_all_permissions } from "@/api/permissions";
    import addPosition from "@/components/settings/permissions/addPosition.vue";
    export default {
        name: "NewPermissions",
        components: {
            addPosition
        },
        data() {
            return {
                positions: [],
                permission_categories: [],
                selectedPosition: 0,
                modal: {
                    display: false,
                    type: ""
                }
            };
        },
        methods: {
            openAddPositionModal() {
                this.modal.display = true;
                this.modal.type = "add_position";
            },
            async get_company_positions() {
                this.positions = await company_positions_list(this.$cookies.get("userToken"), this.$cookies.get("c_id"));
            },
            async addPosition() {
                console.log("Add Position");
            },
            async get_all_permissions() {
                this.permission_categories = await get_all_permissions(this.$cookies.get("userToken"), this.$cookies.get("c_id"));
            },
            beforeEnter(el) {
                el.style.maxHeight = '0';
            },
            beforeLeave(el) {
                el.style.maxHeight = el.scrollHeight + 'px';
            }
        },
        created() {
            this.get_company_positions();
            this.get_all_permissions();
        },
    }
</script>

<style scoped>
    .accordion-enter-active, .accordion-leave-active {
        transition: max-height 0.5s ease;
        overflow: hidden;
    }
    .accordion-enter, .accordion-leave-to {
        max-height: 0;
    }
    .accordion-enter-to, .accordion-leave {
        max-height: 500px; /* Adjust if content exceeds this */
    }
</style>